/* eslint-disable no-console */
import { makeAutoObservable, runInAction } from 'mobx';

import Image from '../models/Image';
import type { CampaignRootStore } from './CampaignRootStore';

class ImageStore {
  socket: WebSocket | null = null;

  imageMetadata: Image | undefined = undefined;

  isConnected = false;

  lastCreatedUrl: string | undefined = undefined;

  imageMetadataHistory: Image[] = [];

  // track the current index of the image displayed
  currentIndex = 0;

  liveMode = true;

  rootStore: CampaignRootStore;

  constructor(rootStore: CampaignRootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
  }

  updateSocket(socket: WebSocket | null): void {
    this.socket = socket;
  }

  updateIsConnected(isConnected: boolean): void {
    this.isConnected = isConnected;
  }

  updateImageMetadata(imageMetadata: Image): void {
    this.imageMetadata = imageMetadata;
    this.imageMetadataHistory.push(imageMetadata);
    if (this.liveMode) {
      this.currentIndex = this.imageMetadataHistory.length - 1;
    }
  }

  updateLastCreatedUrl(lastCreatedUrl: string): void {
    this.lastCreatedUrl = lastCreatedUrl;
  }

  updateLiveMode(liveMode: boolean): void {
    this.liveMode = liveMode;
  }

  // Fetch the image and return a new object URL
  static async fetchImage(imageUrl: string): Promise<string | undefined> {
    try {
      const response = await fetch(imageUrl);
      if (response.ok) {
        const blob = await response.blob();
        return URL.createObjectURL(blob);
      }
    } catch (error) {
      console.error('Error fetching image:', error);
    }
    return undefined;
  }

  // Action to update the image when 'playing' is true
  async updateImage(): Promise<void> {
    if (this.liveMode && this.imageMetadata) {
      const newUrl = await ImageStore.fetchImage(this.imageMetadata.image_url);
      if (this.lastCreatedUrl) {
        URL.revokeObjectURL(this.lastCreatedUrl);
      }
      runInAction(() => {
        this.lastCreatedUrl = newUrl;
      });
    }
  }

  get currentImageMetadata(): Image | undefined {
    if (this.imageMetadataHistory.length === 0) return undefined;
    return this.imageMetadataHistory[this.currentIndex];
  }

  // Navigation actions : These are just place holders for now, but will be used in the future
  nextImage(): void {
    if (this.currentIndex < this.imageMetadataHistory.length - 1) {
      this.currentIndex++;
      this.liveMode = false; // user is manually navigating the images
    }
  }

  previousImage(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.liveMode = false;
    }
  }

  pause(): void {
    this.liveMode = false;
  }

  goToImage(imageId: string): void {
    const index = this.imageMetadataHistory.findIndex((image) => image.image_id === imageId);
    if (index >= 0) {
      this.currentIndex = index;
      this.liveMode = false;
    }
  }

  goToLatest(): void {
    this.currentIndex = this.imageMetadataHistory.length - 1;
    this.liveMode = true;
  }

  // Optional cleanup when needed
  dispose(): void {
    if (this.socket) {
      this.socket.close();
    }
  }
}

export default ImageStore;
